import { useLayoutEffect, useState } from "react";

const DEFAULT_LAYOUT = {
    anchor: "map",
    centerX: 0,
    fixedRight: 10,
    fixedBottom: "calc(24px + env(safe-area-inset-bottom))",
};

export function getMapWrapAnchorLayout(mapWrapEl) {
    if (!mapWrapEl) return DEFAULT_LAYOUT;

    const mapRect = mapWrapEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const isMapBottomVisible = mapRect.bottom > 0 && mapRect.bottom <= viewportHeight;

    return {
        anchor: isMapBottomVisible ? "map" : "viewport",
        centerX: mapRect.left + mapRect.width / 2,
        fixedRight: Math.max(10, viewportWidth - mapRect.right + 10),
        fixedBottom: DEFAULT_LAYOUT.fixedBottom,
    };
}

export function useMapWrapAnchor(ref, active = true) {
    const [layout, setLayout] = useState(DEFAULT_LAYOUT);

    useLayoutEffect(() => {
        if (!active) {
            setLayout(DEFAULT_LAYOUT);
            return undefined;
        }

        const update = () => {
            if (!ref.current) return;
            setLayout(getMapWrapAnchorLayout(ref.current));
        };

        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, { passive: true });
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update);
        };
    }, [ref, active]);

    return layout;
}

const MAP_CONTROLS_VIEWPORT_CLASS = "map-controls-anchor--viewport";

export function bindMapControlsViewportAnchor(anchorEl) {
    if (!anchorEl) return () => {};

    const update = () => {
        const layout = getMapWrapAnchorLayout(anchorEl);
        if (layout.anchor === "viewport") {
            anchorEl.classList.add(MAP_CONTROLS_VIEWPORT_CLASS);
            anchorEl.style.setProperty("--map-controls-fixed-right", `${layout.fixedRight}px`);
            anchorEl.style.setProperty("--map-controls-fixed-bottom", layout.fixedBottom);
            return;
        }

        anchorEl.classList.remove(MAP_CONTROLS_VIEWPORT_CLASS);
        anchorEl.style.removeProperty("--map-controls-fixed-right");
        anchorEl.style.removeProperty("--map-controls-fixed-bottom");
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, { passive: true });

    return () => {
        window.removeEventListener("resize", update);
        window.removeEventListener("scroll", update);
        anchorEl.classList.remove(MAP_CONTROLS_VIEWPORT_CLASS);
        anchorEl.style.removeProperty("--map-controls-fixed-right");
        anchorEl.style.removeProperty("--map-controls-fixed-bottom");
    };
}
